import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AsyncBoundary, Card, Chip, Notice } from '@kollez/design-system';
import { formatIndianDate } from '@kollez/shared';
import { useMarkNotifReadBySubject, useMyBadge } from '@/lib/queries';

/**
 * /profile/badges/:id (v1.6 §7.9-7.18) — one credential's provenance page.
 *
 * Reached from:
 *   - The Step 46 `badge_earned` notification's CTA.
 *   - The Step 47 `badge_revoked` notification's CTA (once Step 49 lands, the notification
 *     writers upgrade the action.url — see companion commit).
 *   - The Profile page BadgeStrip (each chip becomes a link into this page).
 *
 * Answers the four questions a student has about any credential they hold or have held:
 *   1. What is this? (skill, level, taxonomy, certified/verified)
 *   2. Who issued it? (issuer tenant, issuer tier — Kollez tier_1 vs partner tier_2/3)
 *   3. Where did I earn it? (workflow name + version + stage that emitted it)
 *   4. Is it still valid? (status, revocation reason if revoked, expiry if set)
 *
 * `?includeInactive=1` isn't wired at this level because the badge id resolves the row
 * directly — the endpoint returns the badge regardless of status. Revoked/expired badges
 * simply render with a warning banner instead of a healthy-state chip.
 */

const TIER_LABELS: Record<string, string> = {
  tier_1: 'Kollez',
  tier_2: 'Trusted partner',
  tier_3: 'Standard partner',
  tier_4: 'Self-reported',
};

export function BadgeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const q = useMyBadge(id);
  const b = q.data?.badge;

  // Step 53 — landing on this page counts as "reading" any pending notifications for this
  // badge (earned, revoked, expired, superseded). Fires once per mount+id, guarded so
  // React StrictMode's double-invoke in dev doesn't double-fire. Non-fatal: a failed mutation
  // just leaves the pill until the 30s refetch cycle catches up.
  const markRead = useMarkNotifReadBySubject();
  const firedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!id || !b || firedFor.current === id) return;
    firedFor.current = id;
    void markRead.mutateAsync({ kind: 'Badge', id }).catch(() => {
      // Non-fatal — refetch cycle catches the discrepancy.
    });
  }, [id, b, markRead]);

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">
            <Link className="link" to="/profile">← Back to your profile</Link>
          </p>
          <h1 className="h-page">{b?.skill ?? 'Badge'}</h1>
          <p className="subhead">
            {/* Deep-audit fix (Page 26/N): the old subhead said 'v1.6 §7.9 —
                portable credential'. That spec reference belongs in code
                comments, not on a student's screen. Rewrote to describe
                what a portable credential does. */}
            A portable credential. Travels with you across every future application on Kollez.
          </p>
        </div>
      </div>

      <AsyncBoundary
        kind="detail"
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && !b}
        emptyTitle="Badge not found"
        emptyHint="You may have opened a link for a badge that isn't yours, or the record has been removed."
      >
        {b ? (
          <div className="grid gap-3">
            {/* Status banner — non-healthy states surface here so it's the first thing the
                student sees when landing from a revoke notification. */}
            {b.status === 'revoked' ? (
              <Notice tone="warning">
                <b>This badge was revoked{b.revokedAt ? ` on ${formatIndianDate(b.revokedAt)}` : ''}.</b>
                {b.revokedReason
                  ? ` Reason: ${b.revokedReason.replace(/_/g, ' ')}. `
                  : ' '}
                If you accept another qualifying offer, a fresh badge will land here.
              </Notice>
            ) : null}
            {b.status === 'expired' ? (
              <Notice tone="warning">
                <b>This badge expired.</b> Some issuers set validity windows on their
                credentials. Re-take the underlying assessment to renew.
              </Notice>
            ) : null}
            {b.status === 'superseded' ? (
              <Notice>
                <b>This badge was superseded.</b> You earned a higher-level credential for the
                same skill; workflows check the newer badge first.
              </Notice>
            ) : null}

            {/* Headline card — the what */}
            <Card padding="lg">
              <div className="flex flex-wrap items-baseline gap-2 mb-2">
                <h2 className="h-section">{b.skill}</h2>
                <Chip tone={
                  b.status === 'active' || b.status === 'provisional' ? 'success'
                  : b.status === 'revoked' ? 'danger'
                  : 'default'
                }>
                  {b.status}
                </Chip>
                {b.level ? <span className="tag tag-pressed">{b.level}</span> : null}
                {b.certified ? <span className="tag">certified</span> : null}
                {b.verified ? <span className="tag">verified</span> : null}
              </div>
              <p className="cell-sub m-0">
                Taxonomy: <b>{b.taxonomy}</b>
                {typeof b.levelRank === 'number' ? <> · Rank <b>{b.levelRank}</b> <span title="Higher rank = higher-level version of this credential (Beginner → Advanced → Expert).">ⓘ</span></> : null}
                {typeof b.score === 'number' ? <> · Score <b>{b.score}</b> <span title="The score you achieved on the assessment or workflow that awarded this badge.">ⓘ</span></> : null}
              </p>
            </Card>

            {/* Issuer + issuance */}
            <Card padding="lg">
              <h2 className="h-section mb-2">Where it came from</h2>
              <div className="grid gap-1 text-sm">
                <div>
                  <b>Issued by:</b> {b.issuer?.name ?? 'Unknown issuer'}
                  {b.issuerTier ? (
                    <> · <span className="cell-sub">{TIER_LABELS[b.issuerTier] ?? b.issuerTier}</span></>
                  ) : null}
                </div>
                <div><b>Issued on:</b> {formatIndianDate(b.issuedAt)}</div>
                {b.expiresAt ? (
                  <div><b>Expires:</b> {formatIndianDate(b.expiresAt)}</div>
                ) : (
                  <div className="cell-sub">No expiry — this badge doesn't decay.</div>
                )}
              </div>
            </Card>

            {/* Provenance — workflow + stage */}
            {b.provenance?.workflowRun ? (
              <Card padding="lg">
                <h2 className="h-section mb-2">How you earned it</h2>
                {(() => {
                  const wf = b.provenance!.workflowRun!.workflow;
                  const stage = b.provenance!.stageKey;
                  return (
                    <div className="grid gap-1 text-sm">
                      {wf ? (
                        <div>
                          <b>Workflow:</b> {wf.name ?? 'template'}
                          {wf.version ? ` · v${wf.version}` : ''}
                          {wf.scope ? ` · ${wf.scope}` : ''}
                        </div>
                      ) : null}
                      {stage ? (
                        <div>
                          <b>Stage that awarded it:</b> {stage.replace(/_/g, ' ')}
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
              </Card>
            ) : null}

            <p className="helper m-0">
              Show this credential to any workflow that asks for {b.skill}. Kollez validates it
              behind the scenes — no proof upload, no re-test.
            </p>
          </div>
        ) : null}
      </AsyncBoundary>
    </div>
  );
}
